import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { TrendingUp, TrendingDown, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

// --- Probability algorithm --- //
function waveValue(w) {
  if (!w) return 0;
  // 5a, 5b, 5c, 5d, 5e all count as 5
  if (String(w).startsWith("5")) return 5;
  const n = parseInt(w, 10);
  return Number.isFinite(n) ? n : 0;
}

function daysBetween(d1, d2) {
  if (!d1 || !d2) return 0;
  const a = new Date(`${d1}T00:00:00`).getTime();
  const b = new Date(`${d2}T00:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round(Math.abs(b - a) / 86400000);
}

// Lookup table from user's IFS formula (step 5 & 6)
const STRENGTH_TABLE = {
  11: 51, 12: 48, 13: 53, 14: 47, 15: 52,
  21: 49, 22: 52, 23: 47, 24: 52, 25: 48,
  31: 52, 32: 49, 33: 55, 34: 48, 35: 53,
  41: 49, 42: 52, 43: 47, 44: 52, 45: 49,
  51: 51, 52: 48, 53: 52, 54: 46, 55: 51,
};

// User's VBA BAYES macro (inputs 0–100, output 0–100)
function bayes(prior, pro, contra) {
  const p = prior / 100;
  const pr = pro / 100;
  const co = contra / 100;
  const a1 = p * pr;
  const a3 = (1 - p) * co;
  const a4 = a1 + a3;
  if (a4 === 0) return 0;
  return (a1 / a4) * 100;
}

function computeProbability(it) {
  const d = waveValue(it.day_wave); // D
  const w = waveValue(it.week_wave); // E
  const m = waveValue(it.month_wave); // F
  const G = Number(it.current_price);
  const H = Number(it.counter_market_structures || 0);
  const I = Number(it.wave_start_price);
  const J = Number(it.recent_extreme_price);
  const K = Number(it.target);

  if (
    !Number.isFinite(G) || !Number.isFinite(I) ||
    !Number.isFinite(J) || !Number.isFinite(K)
  ) {
    return null;
  }

  // Step 1: days between today and most recent top/bottom
  const today = new Date().toISOString().slice(0, 10);
  const N = daysBetween(today, it.recent_extreme_date);

  // Step 2: days between wave start and most recent top/bottom
  const O = daysBetween(it.wave_start_date, it.recent_extreme_date);

  // Step 3 & 4: combined wave strengths
  const P = Math.abs(d) * 10 + Math.abs(w);
  const R = Math.abs(d) * 10 + Math.abs(m);

  // Step 5 & 6: IFS lookup
  const Q = STRENGTH_TABLE[P] ?? 50;
  const S = STRENGTH_TABLE[R] ?? 50;

  // Step 7: prior based on month wave
  const priorTable = { 1: 75, 2: 55, 3: 90, 4: 55, 5: 60 };
  const T = priorTable[Math.abs(m)] ?? 50;

  // Step 8: recursive Bayes (inner → outer per user's formula)
  let prob = bayes(T, G, J);
  // Original formula used bayes(prob, J, I) which made probability INCREASE the
  // larger the move from wave start to the recent top/bottom. We invert it:
  // bigger move from start → wave is more extended → lower chance it continues.
  if (Number.isFinite(I) && I > 0) {
    const movePct = (Math.abs(J - I) / I) * 100;
    const delta = movePct * 1; // 20% move ≈ moderate downward tilt
    const pro = Math.max(1, 50 - delta);
    const contra = Math.min(99, 50 + delta);
    prob = bayes(prob, pro, contra);
  }
  prob = bayes(prob, K, G);
  prob = bayes(prob, Q, 100 - Q);
  prob = bayes(prob, S, 100 - S);
  prob = bayes(prob, 50 - O / 10, 50 + O / 10);
  prob = bayes(prob, Math.max(1, 80 - O), Math.min(99, 20 + O));
  prob = bayes(prob, 58 - N, 42 + N);
  prob = bayes(prob, 50 - H * 10, 50 + H * 10);

  // Extra: distance of current price from the most recent top/bottom
  // — the further away, the lower the probability the wave still continues.
  // distance_pct ≈ |G - J| / J * 100. Each 1% of distance shifts pro/contra by 3
  // points around 50, so a ~10% pullback fully tilts this term against the trend.
  if (Number.isFinite(J) && J > 0) {
    const distancePct = (Math.abs(G - J) / J) * 100;
    const delta = distancePct * 3;
    const pro = Math.max(1, 50 - delta);
    const contra = Math.min(99, 50 + delta);
    prob = bayes(prob, pro, contra);
  }

  return prob;
}

function WaveCell({ wave, trend }) {
  const isUp = trend === "uptrend";
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono tabular-nums text-sm font-semibold ${
        isUp ? "text-emerald-700" : "text-red-600"
      }`}
    >
      <span>{wave}</span>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
    </span>
  );
}

function ProbBar({ value }) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return <span className="text-sm text-slate-400">—</span>;
  }
  const v = Math.max(0, Math.min(100, value));
  const tone =
    v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-slate-700" : "bg-amber-500";
  return (
    <div className="flex items-center gap-3 min-w-[180px]">
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${tone} transition-all duration-300`}
          style={{ width: `${v}%` }}
        />
      </div>
      <span className="font-mono tabular-nums text-sm font-semibold text-slate-900 w-[60px] text-right">
        {v.toFixed(2)}%
      </span>
    </div>
  );
}

export default function MarketConditionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/market-indices");
      setItems(data);
    } catch (e) {
      if (!silent) {
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail) || "Failed to load market data"
        );
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial fetch on mount (fires when navigating to this page)
  useEffect(() => {
    load({ silent: true });
  }, [load]);

  // Auto-refresh whenever the tab/window regains focus or becomes visible
  useEffect(() => {
    const onFocus = () => load({ silent: true });
    const onVisibility = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  return (
    <div className="space-y-8" data-testid="market-conditions-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <p className="text-slate-600 text-sm leading-relaxed max-w-2xl">
          Snapshot of the major indices with the calculated probability that the
          current trend continues.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => load()}
          disabled={refreshing}
          className="gap-2"
          data-testid="refresh-conditions"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="conditions-table"
      >
        <table className="w-full min-w-[720px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Ticker
              </th>
              <th className="text-center py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Day Wave
              </th>
              <th className="text-center py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Week Wave
              </th>
              <th className="text-center py-4 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Month Wave
              </th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Probability Trend Continues
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-sm text-slate-500">
                  Loading indices...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="py-16 text-center"
                  data-testid="empty-conditions"
                >
                  <div className="text-sm text-slate-500">No indices yet.</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Add some on the "Enter Market Details" page first.
                  </div>
                </td>
              </tr>
            ) : (
              items.map((it) => {
                const prob = computeProbability(it);
                return (
                  <tr
                    key={it.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    data-testid={`conditions-row-${it.ticker}`}
                  >
                    <td className="py-4 px-6">
                      <span className="font-mono font-semibold text-slate-900 text-base tracking-wide">
                        {it.ticker}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <WaveCell wave={it.day_wave} trend={it.day_trend || it.trend} />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <WaveCell wave={it.week_wave} trend={it.week_trend || it.trend} />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <WaveCell wave={it.month_wave} trend={it.month_trend || it.trend} />
                    </td>
                    <td className="py-4 px-6" data-testid={`probability-${it.ticker}`}>
                      <ProbBar value={prob} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

